/** Shared ordinary-chat transport; API credentials remain on the server. */
export class BandiAIChat {
    public conversationId: string = '';
    public state: any = { stage: 'inactive', waiting: false, accepts_csv: false };
    public error: string = '';
    public busy: boolean = false;
    public file: File | null = null;
    public readonly maxFileBytes = 10 * 1024 * 1024;
    private csrf: string = '';
    private controller: AbortController | null = null;
    private timer: any = null;
    private generation: number = 0;
    private destroyed: boolean = false;
    private waitingMessage: any = null;
    private lastReply: string = '';
    private pendingAnswer: any = null;
    private pollRequest: Promise<void> | null = null;

    constructor(private host: {
        messages: () => any[];
        actor?: () => string;
        input: (value: string) => void;
        busy: (value: boolean) => void;
        render: () => Promise<any>;
        completed?: (question: string, result: any, error?: any) => Promise<any>;
    }) {}

    public get active() {
        return !!this.conversationId && !['inactive', 'idle', 'cancelled'].includes(String(this.state?.stage || 'inactive'));
    }

    public snapshot() {
        return this.conversationId ? { id: this.conversationId, state: { ...this.state } } : null;
    }

    public restore(value: any, messages: any[] = [], resume: boolean = true) {
        this.reset();
        const latest = value || [...messages].reverse().find(item => item?.bandiAIConversation)?.bandiAIConversation;
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(latest?.id || ''))) return;
        this.conversationId = latest.id;
        const stage = String(latest.state?.stage || 'inactive');
        this.state = { stage, waiting: !!latest.state?.waiting, accepts_csv: !!latest.state?.accepts_csv, suspended: !!latest.state?.suspended, model_scope: latest.state?.model_scope, accepts_image: !!latest.state?.accepts_image };
        this.waitingMessage = [...messages].reverse().find(item => item?.bandiAIConversation?.id === this.conversationId) || null;
        this.lastReply = String(this.waitingMessage?.content || '');
        if (resume) this.schedule();
    }

    public async selectFile(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        if (!file || this.busy) return;
        this.file = null;
        this.error = '';
        if (!/\.(csv|png|jpe?g)$/i.test(file.name)) this.error = 'CSV 또는 PNG/JPEG 이미지를 첨부해 주세요.';
        else if (!file.size || file.size > this.maxFileBytes) this.error = '내용이 있는 CSV 또는 PNG/JPEG 파일을 최대 10MiB까지 첨부할 수 있습니다.';
        else this.file = file;
        await this.host.render();
    }

    public async clearFile() { this.file = null; this.error = ''; await this.host.render(); }

    public async trySend(message: string, file: File | null = this.file, options: any = {}): Promise<boolean> {
        // The server analyses every text request, so natural requests do not
        // depend on a browser keyword or a separate model panel.
        if (!message.trim() && !file) return false;
        if (this.busy || this.destroyed) return true;
        this.stopTimer();
        if (!this.conversationId) this.conversationId = this.uuid();
        const requestGeneration = ++this.generation;
        const previousPoll = this.pollRequest;
        const question = message || (/\.(png|jpe?g)$/i.test(file?.name || '') ? '첨부한 웨이퍼 이미지의 결함을 분류해 주세요.' : '첨부한 CSV로 이상탐지를 실행해 주세요.');
        const userMessage: any = { role: 'user', kind: 'text', content: question + (file ? '\n첨부: ' + file.name : '') };
        const prepared = !!options.answer;
        const answer: any = options.answer || { role: 'assistant', kind: 'result', content: '질문의 의도와 필요한 데이터를 확인하고 있어요.', question, processing: false };
        const messages = this.host.messages();
        if (!prepared) messages.push(userMessage, answer);
        this.pendingAnswer = answer;
        if (!prepared) this.host.input('');
        if (prepared) this.busy = true;
        else this.setBusy(true);
        this.error = '';
        await this.host.render();
        try {
            // Let an already-running status request release the server lease.
            // Its generation is stale, so it cannot replace this turn's UI.
            if (previousPoll) await previousPoll;
            if (requestGeneration !== this.generation || this.destroyed) return true;
            const result = await this.request(question, '', file, options);
            if (requestGeneration !== this.generation || this.destroyed) return true;
            if (!result.handled) {
                if (file) throw new Error('첨부한 데이터의 모델 분석 요청을 처리하지 못했습니다. 파일을 확인한 뒤 다시 보내 주세요.');
                for (const item of prepared ? [] : [answer, userMessage]) {
                    const index = messages.indexOf(item);
                    if (index >= 0) messages.splice(index, 1);
                }
                if (!prepared) this.host.input(message);
                if (result.conversation_id) this.conversationId = String(result.conversation_id);
                this.state = { ...(result.state || this.state), waiting: false, accepts_csv: false,
                    suspended: !!result.state?.suspended };
                // Keep the selected model/inputs on the server, while this turn
                // continues through the ordinary agent without background polls.
                if (this.waitingMessage) this.waitingMessage.bandiAIConversation = this.snapshot();
                return false;
            }
            if (file === this.file) this.file = null;
            this.waitingMessage = answer;
            await this.apply(result, answer, question);
            return true;
        } catch (error: any) {
            if (requestGeneration !== this.generation || this.destroyed) return true;
            answer.content = error?.name === 'AbortError'
                ? '응답 확인을 중단했습니다. 모델 분석은 서버에서 계속될 수 있으니, 같은 데이터를 다시 보내기 전에 진행 확인을 눌러 주세요.'
                : this.errorText(error);
            answer.bandiAIConversation = this.snapshot();
            this.error = answer.content;
            if (prepared) throw error;
            await this.host.completed?.(question, null, error);
            return true;
        } finally {
            if (requestGeneration === this.generation && !this.destroyed) {
                this.pendingAnswer = null;
                if (prepared) this.busy = false;
                else this.setBusy(false);
                await this.host.render();
                if (!this.error) this.schedule();
            }
        }
    }

    public async suspendForOtherAgent(prepared: boolean = false): Promise<boolean> {
        if (this.busy || this.destroyed) return false;
        const requestGeneration = ++this.generation;
        this.stopTimer();
        if (!this.active || this.state.suspended) return true;
        const previousPoll = this.pollRequest;
        if (prepared) this.busy = true;
        else this.setBusy(true);
        try {
            if (previousPoll) await previousPoll;
            if (requestGeneration !== this.generation || this.destroyed) return false;
            const result = await this.request('', 'suspend');
            if (requestGeneration !== this.generation || this.destroyed) return false;
            this.state = { ...(result.state || this.state), suspended: true, waiting: false, accepts_csv: false };
            if (this.waitingMessage) this.waitingMessage.bandiAIConversation = this.snapshot();
            return true;
        } catch (error: any) {
            if (requestGeneration === this.generation && !this.destroyed) this.error = this.errorText(error);
            return false;
        } finally {
            if (requestGeneration === this.generation && !this.destroyed) {
                if (prepared) this.busy = false;
                else this.setBusy(false);
                await this.host.render();
            }
        }
    }

    public async pause() { this.stopTimer(); if (this.pollRequest) await this.pollRequest; this.stopTimer(); }

    public async checkStatus() { await this.action('status'); }
    public async cancel() { await this.action('cancel'); }

    private async action(action: string, background: boolean = false) {
        if (!this.conversationId || this.busy || this.destroyed || (!background && this.pollRequest)) return;
        this.stopTimer();
        const requestGeneration = this.generation;
        if (!background) this.setBusy(true);
        this.error = '';
        if (!background) await this.host.render();
        try {
            const result = await this.request('', action);
            if (requestGeneration !== this.generation || this.destroyed) return;
            if (result.handled) {
                let answer = this.waitingMessage;
                if (!answer || !this.host.messages().includes(answer) || action === 'cancel') {
                    answer = { role: 'assistant', kind: 'result', content: '', processing: false };
                    this.host.messages().push(answer);
                }
                this.waitingMessage = answer;
                await this.apply(result, answer, answer.question || 'BANDI 모델 진행 확인');
            }
        } catch (error: any) {
            if (requestGeneration !== this.generation || this.destroyed) return;
            this.error = this.errorText(error);
        } finally {
            if (requestGeneration === this.generation && !this.destroyed) {
                if (!background) this.setBusy(false);
                await this.host.render();
                if (!this.error) this.schedule();
            }
        }
    }

    private async apply(result: any, answer: any, question: string) {
        if (result.conversation_id) this.conversationId = String(result.conversation_id);
        this.state = { ...(result.state || {}), waiting: !!result.state?.waiting, accepts_csv: !!result.state?.accepts_csv };
        const reply = String(result.reply || '');
        const changed = reply && (reply !== this.lastReply || answer.content !== reply);
        if (reply) answer.content = reply;
        answer.role = 'assistant';
        answer.kind = 'result';
        answer.processing = false;
        answer.bandiAIConversation = this.snapshot();
        // Persist only tool names and execution states; never transport arguments,
        // credentials, uploaded data or raw provider responses into attribution.
        const tools = Array.isArray(result.tools) ? result.tools.map((tool: any) => {
            const status = String(tool.status || '').toLowerCase();
            return { name: String(tool.name || ''), status: ['success', 'done', 'ok'].includes(status) ? 'completed' : status };
        }) : [];
        answer.bandiAITools = tools;
        answer.actions = tools.map((tool: any) => ({ ...tool }));
        answer.llm = result.llm && typeof result.llm === 'object'
            ? { ...result.llm } : { status: 'not_executed' };
        if (result.execution && typeof result.execution === 'object') answer.execution = { ...result.execution };
        const modelScope = String(result.state?.model_scope || '');
        if (modelScope === 'anomaly' || modelScope === 'wafer') {
            answer.intentLabel = modelScope === 'anomaly' ? '이상탐지' : '웨이퍼 결함 분류';
        }
        const domainTools = tools.filter((tool: any) => tool.name !== 'serving.infer');
        const responseTools = tools.filter((tool: any) => tool.name === 'serving.infer');
        const runStatus = (items: any[]) => items.some(tool => ['failed', 'error', 'blocked'].includes(tool.status))
            ? 'failed' : items.some(tool => ['running', 'pending'].includes(tool.status)) ? 'running'
                : items.length && items.every(tool => tool.status === 'skipped') ? 'skipped' : 'completed';
        answer.agentRuns = domainTools.length || !tools.length ? [{
            id: String(result.execution?.agentId || (domainTools.length ? 'bandi_ai_agent' : 'bandi_ai_dialogue')),
            name: String(result.execution?.agent || (domainTools.length ? 'BANDI Agent' : 'BANDI 안내 Agent')),
            group: domainTools.length ? 'domain_expert' : 'core',
            status: runStatus(domainTools),
            tools: domainTools.map((tool: any) => tool.name)
        }] : [];
        if (responseTools.length) answer.agentRuns.push({
            id: 'response_generation', name: '응답 생성 Agent', group: 'core',
            status: runStatus(responseTools), tools: ['serving.infer']
        });
        this.lastReply = reply;
        if (changed) await this.host.completed?.(question, result);
    }

    private async request(message: string, action: string, file: File | null = null, options: any = {}) {
        const controller = new AbortController();
        this.controller = controller;
        const abort = () => controller.abort();
        options.signal?.addEventListener('abort', abort, { once: true });
        if (options.signal?.aborted) controller.abort();
        let timedOut = false;
        const timeout = window.setTimeout(() => { timedOut = true; controller.abort(); }, 185000);
        try {
            const refreshToken = async () => {
                const session = await this.decode(await fetch(agentApiPath('/wiz/api/page.agent/bandi_ai_chat_session'), {
                    credentials: 'same-origin', cache: 'no-store', signal: controller.signal
                }));
                if (this.host.actor && session.actorId !== this.host.actor()) {
                    if (typeof CustomEvent !== 'undefined') window.dispatchEvent?.(new CustomEvent('nanofab-auth-required'));
                    throw new Error('반디 사용자가 변경되었습니다. 로그인 상태를 다시 확인해 주세요.');
                }
                this.csrf = String(session.csrfToken || '');
                if (!this.csrf) throw new Error('대화 세션을 준비하지 못했습니다. 새로고침 후 다시 시도해 주세요.');
            };
            const form = new FormData();
            form.append('conversation_id', this.conversationId);
            form.append('message', message);
            form.append('request_id', this.uuid());
            if (action) form.append('action', action);
            if (file) form.append('file', file, file.name);
            if (options.context) form.append('context', JSON.stringify(options.context));
            if (options.confirmedReview) form.append('confirmed_review', JSON.stringify(options.confirmedReview));
            const send = async () => this.decode(await (options.transport || fetch)(agentApiPath('/wiz/api/page.agent/bandi_ai_chat'), {
                method: 'POST', credentials: 'same-origin', body: form,
                headers: { 'X-CSRF-Token': this.csrf }, signal: controller.signal
            }, options.onProgress || (() => {})));
            if (!this.csrf) await refreshToken();
            try {
                return await send();
            } catch (error: any) {
                // This guard rejects the request before any tool executes.
                // Retry it once with the same request ID; never replay network,
                // permission or tool failures that might have executed already.
                if (error?.status !== 403 || error?.code !== 'BANDI_AI_CSRF_INVALID'
                    || controller.signal.aborted) throw error;
                this.csrf = '';
                await refreshToken();
                return await send();
            }
        } catch (error: any) {
            if (timedOut) {
                const timeoutError: any = new Error('모델 응답 확인 시간이 초과되었습니다. 실행 상태를 확인한 뒤 다시 시도해 주세요.');
                timeoutError.name = 'TimeoutError';
                timeoutError.code = 'CHAT_EXECUTION_TIMEOUT';
                timeoutError.retryable = false;
                timeoutError.remoteMayContinue = true;
                throw timeoutError;
            }
            throw error;
        } finally {
            window.clearTimeout(timeout);
            options.signal?.removeEventListener('abort', abort);
            if (this.controller === controller) this.controller = null;
        }
    }

    private async decode(response: Response) {
        if (response.status === 401 && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent?.(new CustomEvent('nanofab-auth-required'));
        }
        let envelope: any;
        try { envelope = await response.json(); } catch (_error) { throw new Error('서버 응답을 읽지 못했습니다. 잠시 후 진행 확인을 눌러 주세요.'); }
        if (!response.ok || Number(envelope?.code) !== 200) {
            const payload = envelope?.data || {};
            const detail = payload.error || envelope?.error || {};
            const error: any = new Error(detail.message || payload.message || envelope?.message
                || 'BANDI 요청을 처리하지 못했습니다. 대화 세션과 연결을 확인해 주세요.');
            error.code = String(detail.code || payload.code || '');
            error.status = response.ok ? Number(envelope?.code) || response.status : response.status;
            if (error.status === 401 && response.status !== 401 && typeof CustomEvent !== 'undefined') {
                this.csrf = '';
                window.dispatchEvent?.(new CustomEvent('nanofab-auth-required'));
            }
            if (error.status === 403 && error.code === 'BANDI_AI_CSRF_INVALID') this.csrf = '';
            if (typeof detail.retryable === 'boolean') error.retryable = detail.retryable;
            else if (typeof detail.details?.retryable === 'boolean') error.retryable = detail.details.retryable;
            throw error;
        }
        return envelope.data || {};
    }

    private errorText(error: any) {
        return String(error?.message || 'BANDI 연결을 확인하지 못했습니다. 잠시 후 진행 확인을 눌러 주세요.');
    }

    private setBusy(value: boolean) { this.busy = value; this.host.busy(value); }
    private stopTimer() { if (this.timer) window.clearTimeout(this.timer); this.timer = null; }
    private schedule() {
        this.stopTimer();
        if (!this.destroyed && !this.state.suspended && this.state.waiting && !this.error) {
            this.timer = window.setTimeout(() => {
                const request = this.action('status', true);
                this.pollRequest = request;
                void request.finally(() => { if (this.pollRequest === request) this.pollRequest = null; });
            }, 5000);
        }
    }

    public resume() { this.schedule(); }

    public abort() {
        if (this.controller && this.pendingAnswer && !this.destroyed) {
            this.pendingAnswer.content = '응답 확인을 중단했습니다. 모델 분석은 서버에서 계속될 수 있으니, 같은 데이터를 다시 보내기 전에 진행 확인을 눌러 주세요.';
            this.pendingAnswer.bandiAIConversation = this.snapshot();
            this.error = this.pendingAnswer.content;
        }
        this.pendingAnswer = null;
        this.generation += 1;
        this.stopTimer();
        if (this.controller) this.controller.abort();
        this.controller = null;
        this.setBusy(false);
    }

    public reset() {
        this.abort();
        this.csrf = '';
        this.conversationId = '';
        this.state = { stage: 'inactive', waiting: false, accepts_csv: false };
        this.error = '';
        this.waitingMessage = null;
        this.lastReply = '';
        this.file = null;
    }

    public destroy() { this.destroyed = true; this.abort(); }

    private uuid() {
        if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
        const bytes = crypto.getRandomValues(new Uint8Array(16));
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
        return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
    }
}

/**
 * Preserve standalone WIZ APIs while supporting BANDI /agent/ proxy.
 */
function agentApiPath(path: string): string {
    if (!path.startsWith('/wiz/api/')) {
        throw new Error('Unexpected agent API path');
    }

    if (typeof window === 'undefined') {
        return path;
    }

    const pathname = window.location.pathname || '';
    const embeddedOnBandi =
        pathname === '/agent' ||
        pathname.startsWith('/agent/');

    return embeddedOnBandi ? '/agent' + path : path;
}
